import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Monitor, Wifi, WifiOff,
  Tv, X, Music, BookOpen, MessageSquare, Gift, FileText,
  SkipBack, SkipForward
} from 'lucide-react';
import { BibleSmartSearch, HymnSmartSearch } from '../components/SmartSearchPanels';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND}${url}`;
};

const typeIcons = { welcome: Tv, hymn: Music, bible: BookOpen, announcement: MessageSquare, offering: Gift };
const typeColors = {
  welcome: { bg: 'bg-blue-500/20', text: 'text-blue-300' },
  hymn: { bg: 'bg-amber-500/20', text: 'text-amber-300' },
  bible: { bg: 'bg-blue-500/20', text: 'text-blue-300' },
  announcement: { bg: 'bg-green-500/20', text: 'text-green-300' },
  offering: { bg: 'bg-rose-500/20', text: 'text-rose-300' },
};

const RemoteControl = () => {
  const [code, setCode] = useState('');
  const [validated, setValidated] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [display, setDisplay] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(null);
  const [flow, setFlow] = useState({ items: [], current_index: -1 });
  const [presentation, setPresentation] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    if (urlCode) { setInputCode(urlCode); validateCode(urlCode); }
  }, []);

  useEffect(() => {
    if (!validated) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${wsProtocol}://${new URL(API).host}/api/display/live`;
    const connectWS = () => {
      try {
        wsRef.current = new WebSocket(wsUrl);
        wsRef.current.onopen = () => setConnected(true);
        wsRef.current.onmessage = (e) => {
          try { setDisplay(JSON.parse(e.data)); }
          catch (err) { console.warn('[RemoteControl] bad WS message', err); }
        };
        wsRef.current.onclose = () => { setConnected(false); setTimeout(connectWS, 3000); };
      } catch (err) {
        console.warn('[RemoteControl] WS connect failed, will retry', err);
      }
    };
    connectWS();
    // Poll display + flow state + presentation slideshow state
    const poll = setInterval(() => {
      fetch(`${API}/display/current`).then(r => r.json()).then(setDisplay).catch(() => {});
      fetch(`${API}/display/remote-flow/active`).then(r => r.json()).then(setFlow).catch(() => {});
      fetch(`${API}/display/presentation/state`).then(r => r.json()).then(setPresentation).catch(() => {});
    }, 2000);
    // Fetch flow + presentation immediately
    fetch(`${API}/display/remote-flow/active`).then(r => r.json()).then(setFlow).catch(() => {});
    fetch(`${API}/display/presentation/state`).then(r => r.json()).then(setPresentation).catch(() => {});
    return () => { clearInterval(poll); wsRef.current?.close(); };
  }, [validated]);

  const validateCode = async (c) => {
    try {
      const res = await fetch(`${API}/display/remote/validate/${c}`, { method: 'POST' });
      const data = await res.json();
      if (data.valid) { setCode(c); setValidated(true); setError(''); }
      else { setError('Invalid code. Please try again.'); }
    } catch { setError('Connection error. Please try again.'); }
  };

  const handleAction = async (endpoint, method = 'POST', body = null) => {
    setLoading(endpoint);
    try {
      const opts = { method, headers: { 'Content-Type': 'application/json' } };
      if (body) opts.body = JSON.stringify(body);
      const res = await fetch(`${API}/display/${endpoint}`, opts);
      const data = await res.json();
      // Refresh display + flow
      const [currentRes, flowRes] = await Promise.all([
        fetch(`${API}/display/current`),
        fetch(`${API}/display/remote-flow/active`)
      ]);
      setDisplay(await currentRes.json());
      setFlow(await flowRes.json());
      return data;
    } catch (e) { console.error(e); }
    setLoading(null);
  };

  const content = display?.content || {};
  const type = display?.display_type || 'welcome';
  const isHymn = type === 'hymn';
  const isBible = type === 'bible';
  const hasVerses = isHymn ? (content.verses?.length > 0) : (content.total_verses > 0);
  const currentVerse = isHymn ? (content.current_verse || 0) : (content.current_verse_index || 0);
  const totalVerses = isHymn ? (content.verses?.length || 0) : (content.total_verses || 0);
  const language = isHymn ? (content.language || 'english') : (content.translation || 'english');

  const flowItems = flow?.items || [];
  const flowIndex = flow?.current_index ?? -1;
  const hasFlow = flowItems.length > 0;
  const canGoPrev = hasFlow && flowIndex > 0;
  const canGoNext = hasFlow && flowIndex < flowItems.length - 1;

  const handlePrevVerse = () => {
    if (currentVerse <= 0) return;
    handleAction(isHymn ? `hymn/verse/${currentVerse - 1}` : `bible/verse/${currentVerse - 1}`);
  };
  const handleNextVerse = () => {
    if (currentVerse >= totalVerses - 1) return;
    handleAction(isHymn ? `hymn/verse/${currentVerse + 1}` : `bible/verse/${currentVerse + 1}`);
  };
  const handleJumpVerse = (idx) => {
    handleAction(isHymn ? `hymn/verse/${idx}` : `bible/verse/${idx}`);
  };
  const handleLanguageSwitch = (lang) => {
    if (isHymn) handleAction('show/hymn', 'POST', { hymn_number: content.hymn_number, language: lang });
    else if (isBible) handleAction(`bible/language/${lang}`);
  };
  const handleFlowNav = (direction) => handleAction(`remote-flow/navigate/${direction}`);

  // Code Entry Screen
  if (!validated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-sm w-full text-center border border-white/20">
          <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Monitor className="w-8 h-8 text-purple-300" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Remote Control</h1>
          <p className="text-white/60 text-sm mb-6">Enter the 6-digit code from the admin panel</p>
          <input
            type="text" data-testid="remote-code-input" maxLength={6} placeholder="Enter code"
            value={inputCode} onChange={(e) => setInputCode(e.target.value.toUpperCase())}
            className="w-full text-center text-2xl font-mono tracking-[0.3em] bg-white/10 border-2 border-white/20 rounded-xl px-4 py-4 text-white placeholder-white/30 focus:border-purple-400 focus:outline-none mb-4"
          />
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
          <button data-testid="remote-connect-btn" onClick={() => validateCode(inputCode)} disabled={inputCode.length < 6}
            className="w-full py-3 rounded-xl bg-purple-600 text-white font-bold text-lg hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            Connect
          </button>
        </div>
      </div>
    );
  }

  const tc = typeColors[type] || typeColors.welcome;
  const TypeIcon = typeIcons[type] || FileText;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 pb-8">
      {/* Status Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {connected ? (
            <><Wifi className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 text-xs font-bold">LIVE</span></>
          ) : (
            <><WifiOff className="w-4 h-4 text-amber-400" /><span className="text-amber-400 text-xs font-bold">POLLING</span></>
          )}
        </div>
        <span className="text-white/40 text-xs font-mono">CODE: {code}</span>
      </div>

      {/* Current Display Info */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10" data-testid="remote-display-info">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs px-2 py-1 rounded-full font-bold ${tc.bg} ${tc.text}`}>{type.toUpperCase()}</span>
          {(isHymn || isBible) && (
            <span className={`text-xs px-2 py-1 rounded-full ${language === 'both' ? 'bg-purple-500/20 text-purple-300' : language === 'yoruba' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
              {language === 'both' ? 'Both' : language === 'yoruba' ? 'Yoruba' : 'English'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${tc.bg}`}><TypeIcon className={`w-5 h-5 ${tc.text}`} /></div>
          <div className="flex-1">
            {isHymn && <h2 className="text-white text-lg font-bold">Hymn #{content.hymn_number} — {content.title}</h2>}
            {isBible && <h2 className="text-white text-lg font-bold">{content.reference}</h2>}
            {type === 'welcome' && <h2 className="text-white text-lg font-bold">Welcome Display</h2>}
            {type === 'announcement' && <h2 className="text-white text-lg font-bold">Announcement</h2>}
            {type === 'offering' && <h2 className="text-white text-lg font-bold">Offering</h2>}
            {type === 'blank' && <h2 className="text-white/60 text-lg font-bold">Screen Cleared</h2>}
          </div>
        </div>
      </div>

      {/* Presentation slideshow preview — shows whatever slide is currently
          on the TV during the Rules & Tenets / Inspirational / Announcements
          / Schedule auto-cycling slideshow. The phone shows the FULL text so
          the operator/usher can read along. */}
      {(() => {
        if (!presentation || !presentation.slides || presentation.total_slides === 0) return null;
        const idx = presentation.current_slide ?? 0;
        const slide = presentation.slides[idx];
        if (!slide) return null;
        const slideTypeLabel = {
          welcome: 'WELCOME',
          rule: 'RULES & TENETS',
          quote: 'BIBLE QUOTE',
          inspirational: 'INSPIRATIONAL MESSAGE',
          announcement: 'ANNOUNCEMENT',
          schedule: 'WEEKLY SCHEDULE',
        }[slide.type] || (slide.type || 'SLIDE').toUpperCase();
        const slideAccent = {
          welcome: 'from-indigo-500/30 to-blue-500/20 text-blue-200 border-blue-300/40',
          rule: 'from-purple-500/30 to-violet-500/20 text-purple-200 border-purple-300/40',
          quote: 'from-sky-500/30 to-cyan-500/20 text-cyan-200 border-cyan-300/40',
          inspirational: 'from-amber-500/30 to-yellow-500/20 text-amber-100 border-amber-300/40',
          announcement: 'from-emerald-500/30 to-green-500/20 text-emerald-100 border-emerald-300/40',
          schedule: 'from-rose-500/30 to-pink-500/20 text-rose-100 border-rose-300/40',
        }[slide.type] || 'from-slate-500/30 to-slate-500/20 text-slate-100 border-slate-300/40';
        return (
          <div
            className={`relative overflow-hidden rounded-2xl p-4 mb-4 border bg-gradient-to-br ${slideAccent}`}
            data-testid="remote-presentation-slide"
          >
            <div className="flex items-center justify-between mb-2 gap-2">
              <span className="text-[10px] font-black tracking-[0.18em] px-2.5 py-1 rounded-full bg-white/15 border border-white/15">
                {slideTypeLabel}
              </span>
              <span className="text-[11px] font-mono text-white/60">
                Slide {idx + 1} / {presentation.total_slides}
              </span>
            </div>
            {slide.title && (
              <h3 className="text-white text-lg font-black leading-tight mb-1.5" data-testid="remote-presentation-title">
                {slide.title}
              </h3>
            )}
            {slide.bible_reference && (
              <p className="text-amber-200 text-xs font-bold tracking-wider mb-2 italic">
                — {slide.bible_reference}
              </p>
            )}
            {slide.content && (
              <p
                className="text-white/90 text-sm whitespace-pre-wrap leading-relaxed"
                data-testid="remote-presentation-content"
              >
                {slide.content}
              </p>
            )}
            {slide.flyer_url && (
              <div className="mt-3 rounded-xl overflow-hidden border border-white/15 bg-black/30">
                <img
                  src={resolveImageUrl(slide.flyer_url)}
                  alt={slide.title || 'Slide flyer'}
                  className="w-full max-h-72 object-contain bg-black/40"
                  data-testid="remote-presentation-flyer"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        );
      })()}

      {/* Announcement preview — shows the title, body and flyer being broadcast on the TV */}
      {type === 'announcement' && (
        <div
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10"
          data-testid="remote-announcement-preview"
        >
          {content.title && (
            <h3
              className="text-white text-base font-bold mb-1.5 leading-snug"
              data-testid="remote-announcement-title"
            >
              {content.title}
            </h3>
          )}
          {content.description && (
            <p
              className="text-white/80 text-sm whitespace-pre-wrap mb-3 leading-relaxed"
              data-testid="remote-announcement-description"
            >
              {content.description}
            </p>
          )}
          {(content.date || content.time || content.venue) && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {[content.date, content.time, content.venue].filter(Boolean).map((d, i) => (
                <span
                  key={i}
                  className="text-amber-200 text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full border border-amber-300/40 bg-amber-500/10"
                >
                  {d}
                </span>
              ))}
            </div>
          )}
          {(() => {
            const flyer =
              content.flyer?.image_url ||
              content.flyer_url ||
              content.image_url ||
              content.background_url ||
              '';
            if (!flyer) return null;
            return (
              <div className="rounded-xl overflow-hidden border border-white/15 bg-black/30">
                <img
                  src={resolveImageUrl(flyer)}
                  alt={content.title || 'Announcement flyer'}
                  className="w-full max-h-72 object-contain bg-black/40"
                  data-testid="remote-announcement-flyer"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* Smart + Voice Search (Bible) */}
      <div className="mb-4">
        <BibleSmartSearch compact />
      </div>
      {/* Smart Search (Hymn) */}
      <div className="mb-4">
        <HymnSmartSearch compact />
      </div>

      {/* ── SERVICE FLOW NAVIGATION ── */}
      {hasFlow && (
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10" data-testid="remote-flow-nav">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/50 text-xs font-bold tracking-wider">SERVICE FLOW</p>
            <p className="text-white text-sm font-bold">{flowIndex + 1} / {flowItems.length}</p>
          </div>

          {/* Prev / Next Flow Item */}
          <div className="flex gap-3 mb-3">
            <button
              data-testid="remote-flow-prev"
              onClick={() => handleFlowNav('previous')}
              disabled={!canGoPrev || loading === 'service-flow/navigate/previous'}
              className="flex-1 py-3.5 rounded-xl bg-white/10 text-white flex items-center justify-center gap-2 font-bold hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
            >
              <SkipBack className="w-5 h-5" /> PREV
            </button>
            <button
              data-testid="remote-flow-next"
              onClick={() => handleFlowNav('next')}
              disabled={!canGoNext || loading === 'service-flow/navigate/next'}
              className="flex-1 py-3.5 rounded-xl bg-indigo-600 text-white flex items-center justify-center gap-2 font-bold hover:bg-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95"
            >
              NEXT <SkipForward className="w-5 h-5" />
            </button>
          </div>

          {/* Flow Item List */}
          <div className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-thin">
            {flowItems.map((item, i) => {
              const Icon = typeIcons[item.type] || FileText;
              const c = typeColors[item.type] || typeColors.welcome;
              const isActive = i === flowIndex;
              return (
                <div
                  key={item.id || i}
                  data-testid={`remote-flow-item-${i}`}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-indigo-600/40 border border-indigo-400/50'
                      : i < flowIndex ? 'bg-white/5 opacity-50' : 'bg-white/5'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${c.bg}`}><Icon className={`w-3.5 h-3.5 ${c.text}`} /></div>
                  <span className={`flex-1 text-sm truncate ${isActive ? 'text-white font-bold' : 'text-white/70'}`}>
                    {item.title}
                  </span>
                  {isActive && <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10" data-testid="remote-quick-actions">
        <p className="text-white/50 text-xs font-bold mb-3 tracking-wider">QUICK ACTIONS</p>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="remote-show-welcome" onClick={() => handleAction('show/welcome')}
            className={`py-3 rounded-xl text-sm font-bold transition-all active:scale-95 flex items-center justify-center gap-2 ${type === 'welcome' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
            <Tv className="w-4 h-4" /> Welcome
          </button>
          <button data-testid="remote-show-blank" onClick={() => handleAction('show/blank')}
            className={`py-3 rounded-xl text-sm font-bold transition-all active:scale-95 flex items-center justify-center gap-2 ${type === 'blank' ? 'bg-slate-600 text-white shadow-lg' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
            <X className="w-4 h-4" /> Clear Screen
          </button>
        </div>
      </div>

      {/* Language Toggle */}
      {(isHymn || isBible) && (
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10" data-testid="remote-language-toggle">
          <p className="text-white/50 text-xs font-bold mb-2 tracking-wider">LANGUAGE</p>
          <div className="flex gap-2">
            {['english', 'both', 'yoruba'].map((lang) => (
              <button key={lang} data-testid={`remote-lang-${lang}`} onClick={() => handleLanguageSwitch(lang)}
                className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${language === lang ? 'bg-purple-600 text-white shadow-lg' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>
                {lang === 'english' ? 'English' : lang === 'both' ? 'Both' : 'Yoruba'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Verse Navigation */}
      {(isHymn || isBible) && hasVerses && (
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-4 border border-white/10" data-testid="remote-verse-nav">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/50 text-xs font-bold tracking-wider">VERSE NAVIGATION</p>
            <p className="text-white text-sm font-bold">{currentVerse + 1} / {totalVerses}</p>
          </div>
          <div className="flex gap-3 mb-3">
            <button data-testid="remote-prev-verse" onClick={handlePrevVerse} disabled={currentVerse <= 0}
              className="flex-1 py-4 rounded-xl bg-white/10 text-white flex items-center justify-center gap-2 font-bold text-lg hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95">
              <ChevronLeft className="w-6 h-6" /> PREV
            </button>
            <button data-testid="remote-next-verse" onClick={handleNextVerse} disabled={currentVerse >= totalVerses - 1}
              className="flex-1 py-4 rounded-xl bg-purple-600 text-white flex items-center justify-center gap-2 font-bold text-lg hover:bg-purple-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95">
              NEXT <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {Array.from({ length: totalVerses }, (_, i) => (
              <button key={i} data-testid={`remote-verse-${i}`} onClick={() => handleJumpVerse(i)}
                className={`w-11 h-11 rounded-xl text-sm font-bold transition-all active:scale-90 ${i === currentVerse ? 'bg-purple-600 text-white shadow-lg scale-110' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Guidance when no flow and no hymn/bible */}
      {!hasFlow && !isHymn && !isBible && (
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-5 border border-white/5 text-center" data-testid="remote-guidance">
          <Music className="w-8 h-8 text-white/20 mx-auto mb-2" />
          <p className="text-white/40 text-sm">Start a service from the admin panel to see the workflow here.</p>
          <p className="text-white/30 text-xs mt-1">Activate items in the Service Flow tab to control them remotely.</p>
        </div>
      )}
    </div>
  );
};

export default RemoteControl;
